import React, { useEffect, useRef } from "react";

const scriptId = "google-maps-places-script";
let scriptPromise = null;

const resolveApiKey = (propKey) =>
  propKey ||
  (typeof import.meta !== "undefined" &&
    (import.meta.env?.PUBLIC_GOOGLE_MAPS_API_KEY ||
      import.meta.env?.REACT_APP_GOOGLE_MAPS_API_KEY)) ||
  (typeof process !== "undefined" &&
    (process.env?.PUBLIC_GOOGLE_MAPS_API_KEY ||
      process.env?.REACT_APP_GOOGLE_MAPS_API_KEY)) ||
  (typeof window !== "undefined" &&
    (window.PUBLIC_GOOGLE_MAPS_API_KEY || window.REACT_APP_GOOGLE_MAPS_API_KEY)) ||
  (typeof localStorage !== "undefined" &&
    localStorage.getItem("GOOGLE_MAPS_API_KEY"));

/**
 * Lazy-load Google Places library once and cache the promise.
 * @returns {Promise<any>}
 */
const loadGoogleMapsPlaces = (apiKey) => {
  if (typeof window === "undefined")
    return Promise.reject(new Error("No window"));
  if (window.google?.maps?.places)
    return Promise.resolve(window.google.maps.places);

  if (scriptPromise) return scriptPromise;
  const key = resolveApiKey(apiKey);
  if (!key) {
    console.warn("Google Places: missing API key, skipping autocomplete load.");
    return Promise.reject(new Error("Missing Google Maps API key"));
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", () =>
        resolve(window.google.maps.places)
      );
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps.places);
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Lightweight text input that wires Google Places Autocomplete to emit
 * structured selection payloads while still allowing manual edits.
 * @param {{ stop: any, onPlaceSelect?: function, onManualChange?: function, placeholder?: string, apiKey?: string, disabled?: boolean }} props
 * @returns {JSX.Element}
 */
export default function PlacesAutocompleteInput({
  stop,
  onPlaceSelect,
  onManualChange,
  placeholder = "Search address...",
  apiKey,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const disabledRef = useRef(disabled);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    let autocomplete;

    loadGoogleMapsPlaces(apiKey)
      .then(() => {
        if (!inputRef.current || !window.google?.maps?.places) return;
        autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            fields: ["formatted_address", "geometry", "place_id", "name"],
          }
        );
        autocomplete.addListener("place_changed", () => {
          if (disabledRef.current) return;
          const place = autocomplete.getPlace();
          if (!place) return;
          const lat = place.geometry?.location?.lat();
          const lng = place.geometry?.location?.lng();

          onPlaceSelectRef.current?.({
            address:
              place.formatted_address ||
              place.name ||
              inputRef.current?.value ||
              "",
            lat: typeof lat === "function" ? lat() : lat,
            lng: typeof lng === "function" ? lng() : lng,
            placeId: place.place_id || "",
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("Google Places autocomplete failed to load", err);
      });

    return () => {
      if (autocomplete) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocomplete);
      }
    };
  }, [apiKey]);

  return (
    <input
      ref={inputRef}
      className="form-control form-control-sm border-0 responder-input"
      placeholder={placeholder}
      value={stop.address || ""}
      onChange={(e) => {
        if (disabled) return;
        onManualChange?.(e.target.value);
      }}
      disabled={disabled}
    />
  );
}
